import { bumpCustomColormapRevision, deleteCustomColormapRevision } from "@/cache/customColormapRevision";
import type { ColormapCreate } from "@mm/metx-workbench.meteomatics.com";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QueryKey } from "..";
import { Api } from "../Api";

export interface CustomColormapLight {
  id: string;
  label: string;
}

export interface CustomColormap {
  id: string;
  label: string;
  type?: string;
  stops: [number, string][];
}

type UseGetCustomColormapOptions = {
  enabled?: boolean;
  refetchOnMount?: boolean | "always";
  onSuccess?: (colormap: CustomColormap) => void;
  onError?: (error: Error) => void;
};

export const useGetCustomColormaps = (shouldFetch = true) => {
  const { data, isLoading, error } = useQuery<CustomColormapLight[], Error>(
    [QueryKey.CUSTOM_COLORMAP_LIST],
    () => Api.colormaps.colormapsByUser(),
    {
      staleTime: Number.POSITIVE_INFINITY,
      enabled: shouldFetch,
    },
  );
  return { list: data, isLoading, error };
};

export const useGetCustomColormap = (
  colormapId: string | undefined,
  shouldFetch = true,
  options?: UseGetCustomColormapOptions,
) => {
  const isEnabled = shouldFetch && Boolean(colormapId) && (options?.enabled ?? true);
  const { data, isLoading, error } = useQuery<CustomColormap, Error>(
    [QueryKey.CUSTOM_COLORMAP, colormapId],
    // biome-ignore lint/style/noNonNullAssertion: not enabled if there is no id
    () => Api.colormaps.colormapsGet({ colormapId: colormapId! }),
    {
      staleTime: Number.POSITIVE_INFINITY,
      enabled: isEnabled,
      refetchOnMount: options?.refetchOnMount,
      onSuccess: options?.onSuccess,
      onError: options?.onError,
    },
  );
  return { colormap: data, isLoading, error };
};

export const useCreateCustomColormap = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (colormap: ColormapCreate) => Api.colormaps.colormapsCreate({ colormapCreate: colormap }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.CUSTOM_COLORMAP_LIST] });
      if (created?.id) {
        queryClient.invalidateQueries({ queryKey: [QueryKey.CUSTOM_COLORMAP, created.id] });
      }
    },
  });
};

export const useUploadCustomColormap = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ fileName, file }: { fileName: string; file: Blob }) => {
      const uploadFile = file instanceof File ? file : new File([file], fileName);
      const name = fileName.trim() || undefined;

      return await Api.colormaps.colormapsUpload({
        file: uploadFile,
        name,
      });
    },
    onSuccess: (uploaded) => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.CUSTOM_COLORMAP_LIST] });
      if (uploaded?.id) {
        queryClient.invalidateQueries({ queryKey: [QueryKey.CUSTOM_COLORMAP, uploaded.id] });
      }
    },
  });
};

export const useUpdateCustomColormap = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ colormapId, data }: { colormapId: string; data: Partial<ColormapCreate> }) =>
      Api.colormaps.colormapsUpdate({ colormapId, requestBody: data }),
    onSuccess: (_updated, variables) => {
      bumpCustomColormapRevision(variables.colormapId);
      queryClient.invalidateQueries({ queryKey: [QueryKey.CUSTOM_COLORMAP_LIST] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.CUSTOM_COLORMAP, variables.colormapId] });
    },
  });
};

export const useDeleteCustomColormap = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (colormapId: string) => Api.colormaps.colormapsDelete({ colormapId }),
    onSuccess: (_deleted, colormapId) => {
      deleteCustomColormapRevision(colormapId);
      queryClient.invalidateQueries({ queryKey: [QueryKey.CUSTOM_COLORMAP_LIST] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.CUSTOM_COLORMAP, colormapId] });
    },
  });
};
